/**
 * 上传速率追踪器
 * 用于计算实时上传速率和剩余时间
 */

export interface UploadStats {
  speed: number          // 上传速率（字节/秒）
  speedFormatted: string // 格式化的速率（如 "1.5 MB/s"）
  progress: number       // 进度百分比（0-100）
  loaded: number         // 已上传字节数
  total: number          // 总字节数
  remaining: number      // 剩余时间（秒）
  remainingFormatted: string // 格式化的剩余时间（如 "2分30秒"）
}

export class UploadSpeedTracker {
  private startTime: number = 0
  private lastTime: number = 0
  private lastLoaded: number = 0
  private samples: number[] = []
  private readonly maxSamples = 10
  
  constructor() {
    this.reset()
  }
  
  reset() {
    this.startTime = Date.now()
    this.lastTime = this.startTime
    this.lastLoaded = 0
    this.samples = []
  }
  
  update(loaded: number, total: number): UploadStats {
    const now = Date.now()
    const timeDiff = (now - this.lastTime) / 1000
    const loadedDiff = loaded - this.lastLoaded
    
    let instantSpeed = 0
    if (timeDiff > 0) {
      instantSpeed = loadedDiff / timeDiff
    }
    
    if (instantSpeed > 0) {
      this.samples.push(instantSpeed)
      if (this.samples.length > this.maxSamples) {
        this.samples.shift()
      }
    }
    
    const avgSpeed = this.samples.length > 0
      ? this.samples.reduce((sum, s) => sum + s, 0) / this.samples.length
      : 0
    
    const progress = total > 0 ? Math.round((loaded / total) * 100) : 0
    const remaining = avgSpeed > 0 ? (total - loaded) / avgSpeed : 0
    
    this.lastTime = now
    this.lastLoaded = loaded
    
    return {
      speed: avgSpeed,
      speedFormatted: this.formatSpeed(avgSpeed),
      progress,
      loaded,
      total,
      remaining,
      remainingFormatted: this.formatTime(remaining)
    }
  }
  
  private formatSpeed(bytesPerSecond: number): string {
    if (bytesPerSecond === 0) return '0 B/s'
    
    const units = ['B/s', 'KB/s', 'MB/s', 'GB/s']
    let speed = bytesPerSecond
    let unitIndex = 0
    
    while (speed >= 1024 && unitIndex < units.length - 1) {
      speed /= 1024
      unitIndex++
    }
    
    return `${speed.toFixed(2)} ${units[unitIndex]}`
  }
  
  private formatTime(seconds: number): string {
    if (seconds === 0 || !isFinite(seconds)) return '计算中...'
    
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const secs = Math.floor(seconds % 60)
    
    if (hours > 0) {
      return `${hours}小时${minutes}分${secs}秒`
    } else if (minutes > 0) {
      return `${minutes}分${secs}秒`
    } else {
      return `${secs}秒`
    }
  }
  
  getElapsedTime(): number {
    return (Date.now() - this.startTime) / 1000
  }
  
  getElapsedTimeFormatted(): string {
    return this.formatTime(this.getElapsedTime())
  }
}

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B'
  
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  let size = bytes
  let unitIndex = 0
  
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024
    unitIndex++
  }
  
  return `${size.toFixed(2)} ${units[unitIndex]}`
}
